/**
 * /m/locations/:locationId — devices at one site. Adopted devices
 * (those with a chassis) link to the mobile port view; the rest render
 * greyed (adoption is a desktop workflow).
 */
import { useQuery } from "@tanstack/react-query";
import { ChevronRight } from "lucide-react";
import { Link, useParams } from "react-router-dom";

import { api } from "../../api/client";
import { Badge } from "../components/Badge";

type DeviceOut = {
  entity_id: string;
  name: string | null;
  ip_address: string | null;
  make: string | null;
  model: string | null;
  device_role_name: string | null;
  technology_hint: string | null;
  adoption_tag: "managed" | "unmanaged-recognised" | "unmanaged-unknown";
  chassis_id: number | null;
};

export function MLocationDevices() {
  const { locationId } = useParams<{ locationId: string }>();
  const q = useQuery<{ devices: DeviceOut[] }>({
    queryKey: ["v2", "location-devices", locationId],
    queryFn: async () =>
      (
        await api.get<{ devices: DeviceOut[] }>(
          `/gigafy/locations/${locationId}/devices`,
        )
      ).data,
    enabled: !!locationId,
  });

  const rows = q.data?.devices ?? [];

  return (
    <div className="flex flex-col gap-2">
      <h2 className="text-xs font-semibold uppercase tracking-wide text-slate-400">
        Devices at this site
      </h2>
      {q.isLoading ? (
        <p className="py-6 text-center text-sm text-slate-500">Loading…</p>
      ) : rows.length === 0 ? (
        <p className="py-6 text-center text-sm text-slate-500">
          No devices cached for this site.
        </p>
      ) : (
        rows.map((d) => {
          const body = (
            <>
              <span className="flex flex-1 flex-col gap-0.5 overflow-hidden">
                <span className="truncate text-sm font-medium">
                  {d.name ?? d.entity_id}
                </span>
                <span className="truncate text-xs text-slate-500">
                  {[d.make, d.model, d.ip_address]
                    .filter(Boolean)
                    .join(" · ") || d.device_role_name || "—"}
                </span>
              </span>
              {d.technology_hint && (
                <Badge tone={d.chassis_id != null ? "success" : "muted"}>
                  {d.technology_hint}
                </Badge>
              )}
              {d.chassis_id != null && (
                <ChevronRight className="h-4 w-4 shrink-0 text-slate-500" />
              )}
            </>
          );
          return d.chassis_id != null ? (
            <Link
              key={d.entity_id}
              to={`/m/chassis/${d.chassis_id}`}
              className="flex min-h-[56px] items-center gap-2 rounded-lg border border-slate-800 bg-slate-900 px-3 py-2 text-slate-100 active:bg-slate-800"
            >
              {body}
            </Link>
          ) : (
            <div
              key={d.entity_id}
              className="flex min-h-[56px] items-center gap-2 rounded-lg border border-slate-800/50 bg-slate-900/40 px-3 py-2 text-slate-500"
              title="Not managed yet — adopt it from the desktop app"
            >
              {body}
            </div>
          );
        })
      )}
    </div>
  );
}
